import { createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";

import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const LipidRawMaterialContext = createContext();

export default LipidRawMaterialContext;

export const LipidRawMaterialProvider = ({ children }) => {
  const api = useAxios();
  const navigate = useNavigate();

  const aliquotIndexRef = useRef(0);

  const getAliquotObj = () => ({
    index: aliquotIndexRef.current++,
    id: '', 
    unique_aliquot_id: '', 
    aliquot_operator_list: [], 
    date_aliquoted: null,
    in_use: false,
  });

  const initValues = {
    today: dayjs(Date.now()),
    lipids: {},
    supplierArray: [],
    operatorArray: [],
    productCodeArray: [],
    lotNumberArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const lipidObj = {
    lipidType: null,
    lipidName: null,
    supplier: null,
    productCode: '', 
    lotNumber: '', 
    numberOfIonisableGroups: null,
    molecularWeight: '',
    CASNumber: '',
  };
  const [lipidRawMaterialData, setLipidRawMaterialData] = useState(lipidObj);
  const [lipidAliquotsArray, setLipidAliquotsArray] = useState([]);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [lipidRawMaterialId, setLipidRawMaterialId] = useState('');
  const [deletedIdArray, setDeletedIdArray] = useState([]);

  useEffect(() => {
    getLipidInitialData();
    getOperators();
  }, []);

  const getLipidInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/lipid/initial/data/get');
      
      if (response.status === 200) {                    
        setInitialValues(prev => ({
          ...prev,
          lipids: response.data.lipid_data,
          supplierArray: response.data.supplier_list,
          productCodeArray: response.data.product_code_list,
          lotNumberArray: response.data.lot_number_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Init Data Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const validateLipid = () => {
    const errors = {};
    
    if (!lipidRawMaterialData?.lipidType) {
      errors['lipidType'] = 'Lipid type is required!';
    }
    if (!lipidRawMaterialData?.lipidName) {
      errors['lipidName'] = 'Lipid name is required!';
    }
    if (!lipidRawMaterialData?.supplier) {
      errors['supplier'] = 'Supplier is required!';
    }
    if (!lipidRawMaterialData?.productCode) {
      errors['productCode'] = 'Product code is required!';
    }
    for (const aliquotItem of lipidAliquotsArray) {      
      const aloquotIndex = aliquotItem?.index;
      if (!aliquotItem['unique_aliquot_id']) {
        errors[`unique_aliquot_id-${aloquotIndex}`] = 'Unique aliquot id is required!';
      }
      if (!aliquotItem['date_aliquoted']) {
        errors[`date_aliquoted-${aloquotIndex}`] = 'Date aliquoted is required!';
      }
      if (aliquotItem['aliquot_operator_list'].length === 0) {
        errors[`aliquot_operator_list-${aloquotIndex}`] = 'Operator is required!';
      }
    }
    
    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validateLipid());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      submitLipidRawMaterials();
    }
  }, [formErrors]);

  const submitLipidRawMaterials = () => {
    if (lipidRawMaterialId === '') {
      createLipidRawMaterials();
    } else {
      updateLipidRawMaterials();
    }
  };

  const createLipidRawMaterials = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/lipid-raw-materials/create', {
        lipid_name: lipidRawMaterialData?.lipidName,
        lipid_type: lipidRawMaterialData?.lipidType,
        supplier: lipidRawMaterialData?.supplier,
        product_code: lipidRawMaterialData?.productCode,
        lot_number: lipidRawMaterialData?.lotNumber,
        number_of_ionisable_groups: lipidRawMaterialData?.numberOfIonisableGroups,
        molecular_weight: lipidRawMaterialData?.molecularWeight,
        cas_number: lipidRawMaterialData?.CASNumber,
        lipid_aliquots_list: lipidAliquotsArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        navigate('/lipid-raw-materials');
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Raw Materials create',
        loading: false,
      });
    }
  };

  const updateLipidRawMaterials = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/lipid-raw-materials/edit', {
        lipid_raw_material_id: lipidRawMaterialId,
        lipid_name: lipidRawMaterialData?.lipidName,
        lipid_type: lipidRawMaterialData?.lipidType,
        supplier: lipidRawMaterialData?.supplier,
        product_code: lipidRawMaterialData?.productCode,
        lot_number: lipidRawMaterialData?.lotNumber,
        number_of_ionisable_groups: lipidRawMaterialData?.numberOfIonisableGroups,
        molecular_weight: lipidRawMaterialData?.molecularWeight,
        cas_number: lipidRawMaterialData?.CASNumber,
        lipid_aliquots_list: lipidAliquotsArray,
        deleted_id_list: deletedIdArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Raw Materials update',
        loading: false,
      });
    }
  };

  const handleAliquoteAdd = () => {
    setLipidAliquotsArray(prev => [...prev, getAliquotObj()]);
  };

  const handleAliquotInputChange = (e, index, key, value) => {    
    let data = [...lipidAliquotsArray];    
    data = data.map((obj) =>
      obj?.index === index
        ? { ...obj, [key]: value }
        : obj
    );
    setLipidAliquotsArray(data);
  };

  const handleAliquotDelete = (index, id) => {
    setDeletedIdArray(prev => [...prev, id]);
    setLipidAliquotsArray(prev => prev.filter(item => item?.index !== index));
  };

  useEffect(() => {
    if (lipidRawMaterialId !== '') getLipidRawMaterialDetails()
  }, [lipidRawMaterialId]);

  const getLipidRawMaterialDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/lipid-raw-materials/details/get', {
          params: {
            lipid_raw_material_id: lipidRawMaterialId
          }
        });
      
      if (response.status === 200) {
        const lipiRawData = response.data.lipid_raw_material_data;
        setLipidRawMaterialData(prev => ({
          ...prev,
          lipidType: lipiRawData.lipid_type,
          lipidName: lipiRawData.lipid_name,
          supplier: lipiRawData.supplier,
          productCode: lipiRawData.product_code,
          lotNumber: lipiRawData.lot_number,
          numberOfIonisableGroups: lipiRawData.number_of_ionisable_groups,
          molecularWeight: lipiRawData.molecular_weight,
          CASNumber: lipiRawData.cas_number,
        }));        
        const lipidAliquotData = [];
        for (const item of response.data.lipid_aliquot_data) {
          item['index'] = aliquotIndexRef.current++;
          lipidAliquotData.push(item);
        };        
        setLipidAliquotsArray(lipidAliquotData);
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Raw Materials Init data Get',
        loading: false,
      });
    }
  };

  const contextData = {
    initialValues: initialValues,
    lipidRawMaterialData: lipidRawMaterialData,
    setLipidRawMaterialData: setLipidRawMaterialData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    handleAliquotInputChange: handleAliquotInputChange,
    lipidAliquotsArray: lipidAliquotsArray,
    handleAliquotDelete: handleAliquotDelete,
    handleAliquoteAdd: handleAliquoteAdd,
    setLipidRawMaterialId: setLipidRawMaterialId,
  };

  return (
    <LipidRawMaterialContext.Provider value={contextData}>
      {children}
    </LipidRawMaterialContext.Provider>
  )
};